import React, { useState } from "react";

const EMPLOYEE_IDS = {
  "Yassine": 173, "Julien": 147, "Sofia": 341, "Martin": 194,
  "Camille": 389, "Alexandre": 101, "Zoé": 181, "Victor": 328,
  "Samir": 118, "Nadia": 286, "Antoine": 374, "Olivier": 272,
  "Clara": 132, "Hugo": 221, "Thomas": 248, "Lucas": 297,
  "Amira": 312, "Marie": 359,
};

const STORAGE_KEY = "s2p5_eliminated_employees";

function useEliminated() {
  const [eliminated, setEliminated] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? new Set(JSON.parse(stored)) : new Set();
    } catch { return new Set(); }
  });
  const toggleEliminate = (id) => {
    setEliminated(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(Array.from(next))); } catch { }
      return next;
    });
  };
  return { eliminated, toggleEliminate };
}

function EliminateButton({ prenom, eliminated, onToggle }) {
  const id = EMPLOYEE_IDS[prenom];
  if (!id) return null;
  const isElim = eliminated.has(id);
  return (
    <button onClick={() => onToggle(id)}
      className={["inline-flex items-center gap-1 ml-2 px-2 py-0.5 rounded text-[10px] font-mono font-semibold transition-all",
        isElim
          ? "bg-red-500/20 border border-red-500/40 text-red-300 hover:bg-red-500/30"
          : "bg-slate-700/60 border border-slate-600/40 text-slate-400 hover:bg-slate-700 hover:text-slate-200",
      ].join(" ")}
      title={isElim ? `Réactiver ${prenom}` : `Éliminer ${prenom}`}>
      {isElim ? "❌ éliminé" : "○ éliminer"}
    </button>
  );
}

function NotebookEntry({ entry, eliminated, onToggle }) {
  const prenoms = Object.keys(EMPLOYEE_IDS);
  const parts = [];
  let remaining = entry.text;
  let key = 0;
  while (remaining.length > 0) {
    let earliestIdx = -1; let earliestPrenom = null;
    for (const prenom of prenoms) {
      const idx = remaining.indexOf(prenom);
      if (idx !== -1 && (earliestIdx === -1 || idx < earliestIdx)) { earliestIdx = idx; earliestPrenom = prenom; }
    }
    if (earliestIdx === -1) { parts.push(<span key={key++}>{remaining}</span>); break; }
    if (earliestIdx > 0) parts.push(<span key={key++}>{remaining.slice(0, earliestIdx)}</span>);
    parts.push(
      <span key={key++} className="inline-flex items-center flex-wrap">
        <span className="font-semibold text-amber-200">{earliestPrenom}</span>
        <EliminateButton prenom={earliestPrenom} eliminated={eliminated} onToggle={onToggle} />
      </span>
    );
    remaining = remaining.slice(earliestIdx + earliestPrenom.length);
  }
  return (
    <div className="p-4 bg-slate-900/60 border border-amber-700/20 rounded-lg">
      <div className="flex items-baseline gap-3 mb-2">
        <div className="text-xs font-mono font-bold text-amber-400">{entry.time}</div>
        <div className="h-px flex-1 bg-amber-700/20" />
      </div>
      <div className="text-sm text-slate-300 leading-relaxed font-handwriting">{parts}</div>
    </div>
  );
}

export default function NotebookClues({ notebookId, onClose }) {
  const { eliminated, toggleEliminate } = useEliminated();

  const notebooks = {
    notebook1: {
      title: "Carnet de Sophie — Notes fin de journée",
      date: "Jeudi 23 novembre - soir",
      entries: [
        {
          time: "~18h",
          text: "Yassine avait l'air pressé en fin d'après-midi. Il a ramassé ses affaires rapidement et est parti sans vraiment dire au revoir. Bizarre pour lui d'habitude — il est toujours le dernier à traîner."
        },
        {
          time: "~19h",
          text: "Zoé a enfin raccroché son téléphone après ce qui semblait être une longue conversation de travail. Elle avait l'air épuisée en partant, elle a juste agité la main depuis le couloir."
        },
        {
          time: "~19h30",
          text: "Victor est passé devant mon bureau en enfilant son manteau. Il a dit quelque chose comme 'je serai pas là demain au bureau' en passant. Je n'ai pas eu le temps de lui demander pourquoi."
        },
        {
          time: "19h45",
          text: "Je pars. En sortant j'ai croisé Samir, Nadia et Antoine dans la salle de réunion — ils finissent visiblement quelque chose d'urgent. En passant dans le couloir du fond j'ai entendu quelqu'un parler au téléphone, mais je n'ai pas vu qui."
        },
        {
          time: "20h45",
          text: "SMS de Samir : 'Je rentre enfin, bonne nuit 🙂'. Il a quitté le bureau."
        },
        {
          time: "21h15",
          text: "Nadia a posté dans #general : 'Bonne nuit tout le monde, je rentre'. Longue journée pour elle."
        },
        {
          time: "21h30",
          text: "Antoine m'a appelée brièvement — il sortait du bureau et voulait me parler d'un point sur le projet Omega. Je lui ai dit qu'on en parlerait demain."
        },
      ]
    },

    // ── Carnet 2 : registre de sécurité ──────────────────────────────────────
    notebook2: {
      title: "Registre sécurité — Accès nocturnes",
      date: "Nuit du 23 au 24 novembre",
      entries: [
        {
          time: "Note",
          text: "Le registre de sécurité enregistre automatiquement les badges encore actifs dans le bâtiment après 20h00."
        },
        {
          time: "20h15",
          text: "Badges détectés (4) : Olivier G. (ID #272), Samir H. (ID #118), Nadia E. (ID #286), Antoine L. (ID #078)"
        },
        {
          time: "20h47",
          text: "⚠️ INCIDENT TECHNIQUE\nDysfonctionnement du système après 20h47.\nDonnées manquantes."
        },
      ]
    },
    notebook3: {
      title: "Carnet d'observations — Notes RH",
      date: "Semaine 47",
      entries: [
        {
          time: "Note",
          text: "Mes observations informelles de la semaine — pour usage interne uniquement."
        },
        {
          time: "Lundi",
          text: "Thomas ne vient plus au bureau depuis plusieurs semaines. Il gère toutes ses semaines en télétravail — je ne l'ai pas vu physiquement depuis un bon moment."
        },
        {
          time: "Mardi",
          text: "Marie est partie en déplacement client ce matin. 'Direction Lyon pour trois jours' m'a-t-elle dit. Elle revient vendredi. Elle avait l'air stressée, grosse présentation à préparer."
        },
        {
          time: "Mardi",
          text: "Hugo m'a prévenu qu'il serait absent jeudi et vendredi pour une formation obligatoire organisée par son école. Il n'avait pas le choix, le calendrier était fixé depuis des semaines."
        },
        {
          time: "Mercredi",
          text: "Clara est en déplacement pour une conférence ML en région parisienne. Elle revient lundi. Elle m'a laissé une liste de points à surveiller."
        },
        {
          time: "Mercredi",
          text: "Amira est absente jeudi également — démarche au tribunal, suite de son dossier. Elle m'a dit qu'elle revient vendredi matin normalement."
        },
        {
          time: "Jeudi",
          text: "Lucas est en arrêt maladie depuis hier. Il était vraiment à plat en début de semaine. Son manager m'a confirmé qu'il ne reviendrait pas avant lundi."
        },
        {
          time: "Jeudi",
          text: "Yassine avait posé son vendredi en congé depuis la semaine dernière — week-end prolongé en famille. Je comprends mieux pourquoi il était pressé de partir jeudi soir."
        },
        {
          time: "Jeudi",
          text: "Julien et Sofia sont partis ensemble en début de soirée, vers 18h je crois. Ils m'ont dit 'à demain' en passant — ils avaient une réunion tôt vendredi matin."
        },
        {
          time: "Jeudi",
          text: "Martin est parti assez tôt. Il avait l'air fatigué — je crois qu'il était là depuis 8h ce matin. Il a éteint son écran et est parti sans s'attarder."
        },
        {
          time: "Jeudi",
          text: "Alexandre a fini vers 18h30. Il avait une longue réunion produit en fin d'après-midi et est sorti juste après. Je l'ai vu éteindre son écran en passant."
        },
        {
          time: "Jeudi soir",
          text: "Zoé avait une interview presse en soirée — c'est pour ça qu'elle est restée plus tard que d'habitude. Elle gère très bien la communication externe."
        },
        {
          time: "Vendredi",
          text: "Camille avait une présentation client tôt vendredi matin. Je l'ai vue relire ses slides jeudi après-midi. Elle est partie précipitamment en fin de journée pour aller préparer."
        },
        {
          time: "Vendredi",
          text: "Victor était en télétravail vendredi pour une intervention réseau planifiée depuis chez lui. Il m'a envoyé un message jeudi pour prévenir qu'il ne serait plus au bureau."
        },
      ]
    },
  };

  const notebook = notebooks[notebookId];
  if (!notebook) return null;
  const eliminatedCount = eliminated.size;

  return (
    <div className="py-8 px-6 bg-gradient-to-br from-amber-950/20 via-slate-900 to-slate-950 min-h-[70vh]">
      <div className="max-w-2xl mx-auto">
        <div className="bg-amber-100/10 border-2 border-amber-600/30 rounded-t-xl p-6">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-xl font-bold text-amber-200 mb-1">📔 {notebook.title}</div>
              <div className="text-sm text-amber-400/70">{notebook.date}</div>
            </div>
            <div className="text-4xl">✍️</div>
          </div>
        </div>

        {eliminatedCount > 0 && (
          <div className="bg-red-500/10 border-x-2 border-red-500/20 px-4 py-2 flex items-center gap-2">
            <span className="text-xs text-red-300 font-mono">
              {eliminatedCount} employé{eliminatedCount > 1 ? "s" : ""} marqué{eliminatedCount > 1 ? "s" : ""} à éliminer
            </span>
            <span className="text-xs text-slate-500 italic">— visible dans la grille des portraits</span>
          </div>
        )}

        <div className="bg-amber-50/5 border-x-2 border-b-2 border-amber-600/30 rounded-b-xl p-6 space-y-4">
          {notebook.entries.map((entry, idx) => (
            <NotebookEntry key={idx} entry={entry} eliminated={eliminated} onToggle={toggleEliminate} />
          ))}
        </div>

        <div className="mt-3 px-1 text-xs text-slate-500 italic">
          💡 Cliquez sur "○ éliminer" à côté d'un nom pour le marquer — il sera déjà coché dans la grille des portraits.
        </div>

        <button onClick={onClose} className="mt-4 w-full px-4 py-3 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-semibold transition">
          Fermer le carnet
        </button>
      </div>
      <style jsx>{`
        .font-handwriting { font-family: 'Segoe Print', 'Bradley Hand', 'Brush Script MT', cursive; }
      `}</style>
    </div>
  );
}